/**
 * ConfirmDialog: asks before doing something that matters (cockatrice-ui `src/ui/ConfirmDialog.jsx`, ported to
 * hyperscript; PLAN.md §0). Built on Dialog.
 * Danger tone paints the confirm button red and should always come with words that say what is lost.
 * `confirmText` makes the person type a word first (for deleting an account).
 */
import './css/confirm-dialog.css';
import { createEffect, createSignal, createUniqueId } from 'solid-js';
import Dialog from './Dialog.ts';
import { uiText } from './text.ts';
import { component, el, when } from './solid.ts';
import type { Child, On } from './solid.ts';

export interface ConfirmDialogProps {
  /** Show the dialog. */
  readonly open?: boolean | undefined;
  /** Cancel, the close button and Escape. */
  readonly onClose?: (() => void) | undefined;
  /** Confirm button, or Enter. */
  readonly onConfirm?: (() => void) | undefined;
  /** A question: "Delete your account?" */
  readonly title: Child;
  /** What happens, and what is kept. */
  readonly message?: Child;
  /** Extra paragraphs under the message. */
  readonly children?: Child;
  /** Names the action: "Delete account". Default "Confirm". */
  readonly confirmLabel?: string | undefined;
  /** Default "Cancel". */
  readonly cancelLabel?: string | undefined;
  /** "danger" paints the confirm button red. Default "default". */
  readonly tone?: 'default' | 'danger' | undefined;
  /** The person must type this exact word before confirming. */
  readonly confirmText?: string | undefined;
  /** Disables both buttons and blocks closing while the action runs. */
  readonly busy?: boolean | undefined;
  /** Confirm label while busy. Default "Working…". */
  readonly busyLabel?: string | undefined;
  /** Small caps line above the title. */
  readonly eyebrow?: Child;
  /** Default "sm". */
  readonly size?: 'sm' | 'md' | 'lg' | undefined;
  /** Render in place for previews. */
  readonly inline?: boolean | undefined;
}

/** The catalog's "Type {word} to confirm", split around the word so the word can sit in a <code>. */
function typeToConfirm(): [string, string] {
  const mark = '\u0000';
  const [before = '', after = ''] = uiText()('ui.confirm-dialog.type', { word: mark }).split(mark);
  return [before, after];
}

export default function ConfirmDialog(props: ConfirmDialogProps): Child {
  const id = createUniqueId();
  const [typed, setTyped] = createSignal('');
  createEffect(() => {
    if (props.open !== true) setTyped('');
  });

  const ready = (): boolean => props.busy !== true && (props.confirmText === undefined || props.confirmText === '' || typed().trim() === props.confirmText);
  const confirm = (): void => {
    if (ready()) props.onConfirm?.();
  };

  return component(Dialog, {
    get open() {
      return props.open;
    },
    get inline() {
      return props.inline;
    },
    get onClose() {
      return props.onClose;
    },
    get title() {
      return props.title;
    },
    get eyebrow() {
      return props.eyebrow;
    },
    get size() {
      return props.size ?? 'sm';
    },
    get tone() {
      return props.tone;
    },
    role: 'alertdialog',
    class: 'ui-confirm-dialog',
    get dismissible() {
      return props.busy !== true;
    },
    closeOnScrim: false,
    onSubmit: confirm,
    get footer() {
      return [
        el(
          'button',
          {
            type: 'button',
            class: 'ui-dialog__btn ui-dialog__btn--secondary',
            onClick: () => props.onClose?.(),
            disabled: () => props.busy,
          },
          () => props.cancelLabel ?? uiText()('ui.confirm-dialog.cancel'),
        ),
        el(
          'button',
          {
            type: 'submit',
            class: () => `ui-dialog__btn ui-dialog__btn--${props.tone === 'danger' ? 'danger' : 'primary'}`,
            disabled: () => !ready(),
            'aria-busy': () => (props.busy === true ? 'true' : undefined),
          },
          () => (props.busy === true ? (props.busyLabel ?? uiText()('ui.confirm-dialog.busy')) : (props.confirmLabel ?? uiText()('ui.confirm-dialog.confirm'))),
        ),
      ];
    },
    get children() {
      return [
        el(
          'div',
          { class: 'ui-dialog__message' },
          when(
            () => Boolean(props.message),
            () => el('p', {}, () => props.message),
          ),
          () => props.children,
        ),
        when(
          () => props.confirmText !== undefined && props.confirmText !== '',
          () =>
            el(
              'label',
              { class: 'ui-dialog__type', for: `ui-confirm-${id}` },
              el('span', {}, () => typeToConfirm()[0], el('code', {}, () => props.confirmText), () => typeToConfirm()[1]),
              el('input', {
                id: `ui-confirm-${id}`,
                type: 'text',
                autocomplete: 'off',
                autocapitalize: 'off',
                spellcheck: false,
                value: typed,
                onInput: (e: On<InputEvent, HTMLInputElement>) => setTyped(e.currentTarget.value),
              }),
            ),
        ),
      ];
    },
  });
}
